import React, { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2BCatalogApi from 'api/b2b/CatalogApiB2b'
import TenantCatalogApi, {
  ITenantCatalogBackendData
} from 'api/b2b/TenantCatalog'
import OrbitSession from 'hooks/useCookies'

import { TenantDetails } from '../../Edit'

import CatalogGroupList from './CatalogGroupList'

interface IAutoCompleteDropdown {
  label: string
  value: string
}

interface IFormInput {
  companyName: string
  currency: string
  products: IAutoCompleteDropdown
}

function CatalogDiscount({
  tenantDetails,
  onSaveSuccess
}: {
  tenantDetails: TenantDetails
  onSaveSuccess: (string) => void
}) {
  const userEmail = OrbitSession.get('email')
  const faberToken = OrbitSession.get('faberToken')

  const [selectedRowId, setSelectedRowId] = useState<string[]>([])
  const [discountValues, setDiscountValues] = useState<Record<string, number>>(
    {}
  )

  const {
    // status,
    data: catalogGroupData,
    isLoading: isCatalogGroupLoading,
    isError: catalogGroupFetchError,
    refetch: refetchCatalogGroup
  } = useQuery(['catalog-group'], async () => {
    const token = OrbitSession.get('faberToken')
    const { data } = await B2BCatalogApi.getCatalogGroup(token)
    return data
  })

  const { control, handleSubmit, setValue, watch } = useForm<IFormInput>({
    defaultValues: {
      companyName: '',
      currency: '',
      products: { value: 'esim', label: 'eSIM' }
    }
  })

  const watchedProducts = watch('products')

  const validateSubmission = (): string | null => {
    if (!selectedRowId) {
      return 'Please select a catalog group'
    }
    if (!tenantDetails?.tenantId) {
      return 'Tenant information is missing'
    }
    if (!userEmail) {
      return 'User email is missing'
    }
    if (!watchedProducts?.value) {
      return 'Please select a product type'
    }
    return null
  }

  const { mutate: createTenantCatalog, isLoading: isAssigningTenantCatalog } =
    useMutation(
      (data: ITenantCatalogBackendData) =>
        TenantCatalogApi.createTenantCatalog(data, faberToken),
      {
        onSuccess: () => {
          toast.success('Catalog created successfully', {
            hideProgressBar: false,
            autoClose: 2000
          })
          onSaveSuccess('#commercials')
        },
        onError: (error) => {
          console.error('API Error:', error)
          toast.error('Failed to create catalog', {
            hideProgressBar: false,
            autoClose: 2000
          })
        }
      }
    )

  const onSubmit: SubmitHandler<IFormInput> = (formData) => {
    const validationError = validateSubmission()

    if (validationError) {
      toast.error(validationError)
      return
    }

    const catalog_group_payload = selectedRowId?.map((id) => ({
      catalog_group_id: id,
      discount_percentage: discountValues[id]?.toString() || '0'
    }))

    const submitData: ITenantCatalogBackendData = {
      tenant_id: tenantDetails.tenantId,
      product_type: formData.products.value,
      created_by: userEmail,
      tenant_catalog_group_discount: catalog_group_payload
    }
    createTenantCatalog(submitData)
  }

  useEffect(() => {
    if (tenantDetails?.name) {
      setValue('companyName', tenantDetails.name)
    }
    if (tenantDetails?.currency) {
      setValue('currency', tenantDetails.currency)
    }
  }, [tenantDetails, setValue])

  if (catalogGroupFetchError) {
    return (
      <Box>
        <Typography color='error'>Error loading data</Typography>
        <Button onClick={() => refetchCatalogGroup()}>Retry</Button>
      </Box>
    )
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
          <Typography sx={{ mr: 2, minWidth: '120px' }}>
            Company Name
          </Typography>
          <Controller
            name='companyName'
            control={control}
            render={({ field }) => (
              <TextField
                id='companyName'
                variant='outlined'
                size='small'
                disabled={true}
                sx={{ width: '300px' }}
                {...field}
              />
            )}
          />
        </Box>

        <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
          <Typography sx={{ mr: 2, minWidth: '120px' }}>Currency</Typography>
          <Controller
            name='currency'
            control={control}
            render={({ field }) => (
              <TextField
                id='currency'
                variant='outlined'
                size='small'
                disabled={true}
                sx={{ width: '100px' }}
                {...field}
              />
            )}
          />
        </Box>

        <Box display='flex' alignItems='center' sx={{ mb: 3 }}>
          <Typography sx={{ mr: 2, minWidth: '120px' }}>Map Product</Typography>
          <Controller
            name='products'
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                disablePortal
                disableClearable
                options={[{ value: 'esim', label: 'eSIM' }]}
                onChange={(_, data) => field.onChange(data)}
                size='small'
                sx={{ width: '300px' }}
                renderInput={(params) => <TextField {...params} />}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <CatalogGroupList
            data={catalogGroupData || []}
            isLoading={isCatalogGroupLoading}
            setSelectedRowId={setSelectedRowId}
            setDiscountValues={setDiscountValues}
            selectedRowId={selectedRowId}
            discountValues={discountValues}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button
            type='submit'
            variant='contained'
            disableElevation
            disabled={
              !selectedRowId ||
              isCatalogGroupLoading ||
              isAssigningTenantCatalog
            }
          >
            Save
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default CatalogDiscount
